import {
  AttestationStatus,
  CareJourneyDetailsResponse,
  CareJourneyEventType,
  CareJourneyStep,
  ScheduleAppointmentJourneyResponse,
  TaskStepName
} from "@/api/dto/care-journey";
import {Fragment, useContext, useEffect, useMemo, useState} from "react";
import {Model, SurveyModel} from "survey-core";
import SurveyJSForm from "@common/survey_js/SurveyJSForm";
import Button, {StyleType} from "patient-ping-remedy/packages/button";
import {
  BannerWrapper,
  ButtonWrapper,
  FormWrapper,
  PreferenceItem,
  PreferenceLabel,
  PreferenceWrapper,
  TableWrapper
} from "@/components/pages/tasks/task_edit/progress/TaskStepForm.styles";
import {CarecoApiContext} from "@/app-context/careco-api-context";
import {useActiveRoster} from "@/store/roster_store";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import TaskStepHistoryTable from "@/components/pages/tasks/task_edit/progress/TaskStepHistoryTable";
import {useAlertStore} from "@/store/alert_store";
import {ToastType} from "patient-ping-remedy/packages/toast";
import {AxiosError} from "axios";
import {parsePhoneNumberFromString} from "libphonenumber-js";
import {GENERICERROR} from "@manual_encounters/constants/BannerMessages";
import Helpers from "@/helpers/helpers";
import {useTaskStepName} from "@/hooks/useTaskStepName";
import {useSurveyJSForm} from "@/hooks/useSurveyJSForm";
import {ClientProfile} from "@/api/dto/client-profile";
import {useAuthStore} from "@/store/auth_store";
import DisplayHelpers from "@/helpers/display_helpers";
import Loading from "@common/Loading";
import Icon, {ICONS} from "patient-ping-remedy/packages/icon";
import {colors, sizes} from "patient-ping-remedy/packages/theme";
import Spacer from "patient-ping-remedy/packages/spacer";
import {css} from "@emotion/css";
import {MixpanelEventType, MixpanelStepEvent} from "@/api/dto/mixpanel";
import {useMixpanel} from "@/hooks/useMixpanel";
import {convertLocalToTimezone, convertTimezoneToLocal} from "@/helpers/timezone_helpers";
import ScheduleAppointmentsStep from "@/components/pages/tasks/task_edit/progress/bh_journey/ScheduleAppointmentsStep";
import {FilterInvalidate} from "@common/FilterComponent";
import {useScheduleViaEmail} from "@/hooks/useScheduleViaEmail";

type Props = {
  careJourneyStep: CareJourneyStep;
  fetching: boolean;
  readOnly: boolean;
  careJourneyDetails: CareJourneyDetailsResponse;
  invalidate: FilterInvalidate;
  patient: ClientProfile;
}

const allowMultipleSubmissionSteps = ["initial_outreach", "schedule_appointment"];

const TaskStepForm = (
  {careJourneyStep, readOnly, fetching, careJourneyDetails, patient, invalidate}: Props) => {
  const {carecoApi} = useContext(CarecoApiContext);
  const currentRoster = useActiveRoster();
  const {currentUser} = useAuthStore();
  const {addAlert} = useAlertStore();
  const {taskStepDisplayValues} = useTaskStepName();
  const {mixpanelEventProperties, getCurrentGroupDetails} = useMixpanel();
  const queryClient = useQueryClient();
  const {scrollToError} = useSurveyJSForm();
  const {scheduleViaEmail, isPending: isSchedulingEmail} = useScheduleViaEmail();
  const [disabled, setDisabled] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [surveyModel, setSurveyModel] = useState<Model | null>(null);
  const allowMultipleSubmission = allowMultipleSubmissionSteps.includes(careJourneyStep.stepName);
  const isScheduledAppointment = careJourneyStep.stepName === TaskStepName.SCHEDULED_APPOINTMENT;
  const isCurrentStep = careJourneyStep.stepName === careJourneyDetails.currentStep;
  const isCompleted = careJourneyStep.status === 'completed';
  const isHumanActor = careJourneyDetails.orchestrationEngineDetails?.actor?.toLowerCase() === 'human';
  const isAttested = careJourneyDetails.orchestrationEngineDetails?.attestation?.status === AttestationStatus.COMPLETED;
  const isAppointmentConfirmation =
      careJourneyStep.stepName === TaskStepName.APPOINTMENT_CONFIRMATION;
  const canEdit =
      isCompleted &&
      isHumanActor &&
      (!isAttested || isAppointmentConfirmation) &&
      !readOnly;

  const restoreFormData = (rawData: Record<string, string>) => {
    const data = { ...rawData };

    if (isScheduledAppointment && data.appointment_timezone && data.pcp_appointment_date) {
      data.pcp_appointment_date = convertTimezoneToLocal(data.pcp_appointment_date, data.appointment_timezone);
    }

    return data;
  };

  const getSubmitData = () => {
    const data = { ...surveyModel?.data, user_id: currentUser?.id };

    if (isScheduledAppointment && data.appointment_timezone && data.pcp_appointment_date) {
      const local = convertTimezoneToLocal(data.pcp_appointment_date, Intl.DateTimeFormat().resolvedOptions().timeZone);
      data.pcp_appointment_date = convertLocalToTimezone(local, data.appointment_timezone);
    }

    return data;
  };

  const {mutate, isPending, error} = useMutation({
    mutationFn: () => {
      // Captured before the request: invalidate() has not refreshed the step yet in onSuccess,
      // and an already-completed step at submit time means this is a resubmission.
      const wasCompleted = isCompleted;
      return carecoApi!.submitCareJourneyStepData(careJourneyDetails.careJourneyInstanceId, currentRoster!.id,
        getSubmitData(), careJourneyStep.schemaGuid!)
        .then(() => wasCompleted);
    },
    onSuccess: async (wasCompleted: boolean) => {
      if(mixpanelEventProperties) {
        const event: MixpanelStepEvent = {
          eventName: MixpanelEventType.BIH_TASK_STEP_FORM_UPDATED,
          eventType: careJourneyDetails.eventType,
          careJourneyName: careJourneyDetails.journeyType,
          stepName: careJourneyStep.stepName,
          ...mixpanelEventProperties!,
          ...getCurrentGroupDetails(currentRoster!)
        };
        carecoApi?.postMixpanelEvent(event);

        // A submission on an already-completed step is a resubmission (Edit path).
        if(wasCompleted) {
          const resubmitEvent: MixpanelStepEvent = {
            eventName: MixpanelEventType.BIH_STEP_RESUBMITTED,
            eventType: careJourneyDetails.eventType,
            careJourneyName: careJourneyDetails.journeyType,
            stepName: careJourneyStep.stepName,
            ...mixpanelEventProperties!,
            ...getCurrentGroupDetails(currentRoster!)
          };
          carecoApi?.postMixpanelEvent(resubmitEvent);
        }
      }
      if(allowMultipleSubmission) {
        surveyModel?.clear();
      }
      addAlert({
        content: (taskStepDisplayValues[careJourneyStep.stepName as TaskStepName] ?? careJourneyStep.stepName) +
          ' form submitted successfully.',
        type: ToastType.SUCCESS
      });
      setEditMode(false);
      invalidate();
    },
    onError: (error : AxiosError) => {
      Helpers.alertOnError(`${GENERICERROR} ${Helpers.traceId(error)}`, error);
    }
  });

  const {mutate: mutateSaveDraft, isPending: isSavingDraft} = useMutation({
    mutationFn: () =>
      carecoApi!.saveCareJourneyStepDraft(
        careJourneyDetails.careJourneyInstanceId,
        currentRoster!.id,
        careJourneyStep.schemaGuid!,
        surveyModel?.data
      ),
    onSuccess: async () => {
      if(mixpanelEventProperties) {
        const event: MixpanelStepEvent = {
          eventName: MixpanelEventType.BIH_STEP_SAVED,
          eventType: careJourneyDetails.eventType,
          careJourneyName: careJourneyDetails.journeyType,
          stepName: careJourneyStep.stepName,
          ...mixpanelEventProperties,
          ...getCurrentGroupDetails(currentRoster!)
        };
        carecoApi?.postMixpanelEvent(event);
      }
      addAlert({
        content: 'Draft saved.',
        type: ToastType.SUCCESS
      });
      invalidate();
    },
    onError: (error: AxiosError) => {
      Helpers.alertOnError(`${GENERICERROR} ${Helpers.traceId(error)}`, error);
    }
  });

  useEffect(() => {
    if (careJourneyStep.surveyJson) {
      const model = new SurveyModel(careJourneyStep.surveyJson);

      // Capture each question's JSON-defined readOnly before we mutate it
      model.getAllQuestions().forEach(question => {
        (question as any)._intrinsicReadOnly = question.readOnly;
      });

      let isDisabled = false;

      const shouldLoadLastAttempt = !allowMultipleSubmission;
      if (shouldLoadLastAttempt) {
        const attempt = careJourneyStep.attempts.at(-1);
        const data = { ...(attempt?.data || {}) };
        for (const [name, value] of Object.entries(data)) {
          if (model.getQuestionByName(name)?.inputType === 'tel' && typeof value === 'string') {
            data[name] = parsePhoneNumberFromString(value)?.nationalNumber ?? value;
          }
        }

        const restored = restoreFormData(data);
        Object.assign(data, restored);

        model.mergeData(data);

        if (!allowMultipleSubmission) {
          isDisabled = careJourneyDetails.orchestrationEngineDetails?.actor?.toLowerCase() !== 'human'
            || careJourneyStep.status === 'completed';
        }
      }

      setDisabled(isDisabled);
      setEditMode(false);
      setSurveyModel(model);

      return () => {
        model.dispose();
      };
    }
  }, [
    careJourneyStep.surveyJson,
    careJourneyStep.status,
    allowMultipleSubmission,
    isScheduledAppointment,
    careJourneyDetails.orchestrationEngineDetails?.actor
  ]);

  useEffect(() => {
    if(!careJourneyStep.surveyJson) {
      setSurveyModel(null);
    }
  }, [careJourneyStep.surveyJson]);

  useEffect(() => {
    const shouldBeReadOnly = isPending || readOnly
      || (!editMode && (disabled || !isCurrentStep));
    surveyModel?.getAllQuestions().forEach(question => {
      question.readOnly = (question as any)._intrinsicReadOnly || shouldBeReadOnly;
    });
  }, [surveyModel, isPending, readOnly, disabled, isCurrentStep, editMode]);


  const handleSubmit = () => {
    const isValid = surveyModel?.validate();
    if (isValid) {
      mutate();
    } else {
      scrollToError();
    }
  };

  const handleViewAttempt = (data: any) => {
    surveyModel?.mergeData(data);
  };

  const intakeAttempt = useMemo(() => {
    let journey = careJourneyDetails;
    if(careJourneyDetails.journeyType === CareJourneyEventType.BH_SCHEDULE_APPOINTMENT) {
      journey = queryClient.getQueryData(
        ['careJourneyDetails', (careJourneyDetails as ScheduleAppointmentJourneyResponse).parentJourneyId]
      ) as CareJourneyDetailsResponse;
    }
    return journey.steps
      .find(step => step.stepName === TaskStepName.INTAKE)?.attempts.at(-1);
  }, [careJourneyDetails]);

  if(fetching) {
    return <Loading></Loading>;
  }

  if(careJourneyStep.stepName === TaskStepName.SCHEDULED_APPOINTMENTS) {
    return <ScheduleAppointmentsStep
      careJourneyStep={careJourneyStep}
      careJourneyDetails={careJourneyDetails}
      readOnly={readOnly}
      patient={patient}
    />;
  }

  if(!surveyModel) {
    return null;
  }

  const disableSubmitButtons = isPending || isSavingDraft || fetching || readOnly
    || (!editMode && (disabled || !isCurrentStep));

  return <Fragment>
    <FormWrapper>
      { careJourneyStep.stepName === TaskStepName.SCHEDULED_APPOINTMENT &&
        <BannerWrapper>
          <div className={css({display: "flex"})}>
            <Icon
              iconClass={ICONS["info-circle"]}
              iconSize={"2x"}
              weight={"fal"}
              color={colors.gray3}
              className={css({marginTop: '0.1rem', padding:'3px'})}
            />
            <Spacer itemWidth={sizes.xSmall}/>
            <span className={css({paddingTop: '0.7rem'})}>
              Scheduling preferences details {" "}
              { intakeAttempt && intakeAttempt.attempted_at &&
                <>(last updated on {DisplayHelpers.formatDate(intakeAttempt.attempted_at)})</>
              }
            </span>
          </div>
          <PreferenceWrapper>
            <PreferenceItem>
              <PreferenceLabel>Time of day</PreferenceLabel>
              {intakeAttempt?.data?.time_of_day ? DisplayHelpers.humanize(intakeAttempt.data.time_of_day) : '-'}
            </PreferenceItem>
            <PreferenceItem>
              <PreferenceLabel>Virtual appointments allowed</PreferenceLabel>
              {typeof intakeAttempt?.data?.virtual_appointments_allowed === 'boolean'
                ? intakeAttempt.data.virtual_appointments_allowed ? 'Yes' : 'No' : '-'}
            </PreferenceItem>
            <PreferenceItem>
              <PreferenceLabel>Days & time that do/ do not work</PreferenceLabel>
              {intakeAttempt?.data?.days_and_time_that_do_or_do_not_work ?? '-'}
            </PreferenceItem>
          </PreferenceWrapper>
        </BannerWrapper>
      }
      <TableWrapper>
        {allowMultipleSubmission && (
          <TaskStepHistoryTable
            attempts={careJourneyStep.attempts}
            stepName={careJourneyStep.stepName}
            journeyType={careJourneyDetails.journeyType}
            surveyModel={surveyModel}
            loading={isPending || fetching}
            error={error}
            onViewAttempt={handleViewAttempt}
          />
        )}
      </TableWrapper>
      <SurveyJSForm surveyModel={surveyModel}/>
    </FormWrapper>
    <ButtonWrapper>
      {!isCompleted && !allowMultipleSubmission && (
        <Button
          styleType={StyleType.SECONDARY}
          disabled={disableSubmitButtons}
          onClick={() => mutateSaveDraft()}>
          Save
        </Button>
      )}
      {isScheduledAppointment && currentRoster && (
        <Button
          styleType={StyleType.SECONDARY}
          disabled={isSchedulingEmail || readOnly}
          onClick={() => scheduleViaEmail(careJourneyDetails, currentRoster, intakeAttempt?.data)}>
          {isSchedulingEmail ? 'Opening Secure Email...' : 'Schedule via Email'}
        </Button>
      )}
      {isCompleted && !editMode && canEdit && (
        <Button
          onClick={() => setEditMode(true)}>
          Edit
        </Button>
      )}
      {(!isCompleted || editMode) && (
        <Button
          disabled={disableSubmitButtons}
          onClick={handleSubmit}>
          Save and submit
        </Button>
      )}
    </ButtonWrapper>
  </Fragment>;
};

export default TaskStepForm;
